/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@shared/ui/utils/tailwind';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  colorOn?: string;
  colorOff?: string;
  label?: string;
  description?: string;
  id?: string;
  /** Names the switch for a reader when there is no visible `label` -- the
   *  row it sits in carries the name instead. Without this every unlabelled
   *  switch in the app announced as an unnamed control, because the prop list
   *  is closed and dropped anything it did not declare. */
  'aria-label'?: string;
}

const sizeConfig = {
  sm: { track: 'h-4 w-7', thumb: 'h-3 w-3', translate: 'data-[state=checked]:translate-x-[14px]' },
  md: { track: 'h-5 w-9', thumb: 'h-3.5 w-3.5', translate: 'data-[state=checked]:translate-x-[20px]' },
};

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'md',
  colorOn = 'bg-[var(--status-success)]',
  colorOff = 'bg-[var(--border-medium)]',
  label,
  description,
  id,
  'aria-label': ariaLabel,
}) => {
  const cfg = sizeConfig[size];

  const switchElement = (
    <SwitchPrimitive.Root
      id={id}
      aria-label={ariaLabel}
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--input-focus-ring)]',
        'disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer',
        cfg.track,
        checked ? colorOn : colorOff,
      )}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none inline-block rounded-full bg-white shadow transition-transform',
          'data-[state=unchecked]:translate-x-0.5',
          cfg.thumb,
          cfg.translate,
        )}
      />
    </SwitchPrimitive.Root>
  );

  if (!label) return switchElement;

  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <label
          htmlFor={id}
          className="text-xs font-medium text-[var(--text-primary)] cursor-pointer"
        >
          {label}
        </label>
        {description && (
          <p className="text-xs text-[var(--text-tertiary)]">{description}</p>
        )}
      </div>
      {switchElement}
    </div>
  );
};

export default Switch;
